import { useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Trash2,
  Plus,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  RotateCcw,
  Loader2,
  ShieldAlert,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Switch } from '@/app/components/ui/switch';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  usePlatformGearModel,
  useUpdatePlatformGearModel,
  useDeletePlatformGearModel,
  usePlatformInspectionSteps,
  useCreatePlatformInspectionStep,
  useUpdatePlatformInspectionStep,
  useDeletePlatformInspectionStep,
  useCopyInspectionStepsFromCategory,
  usePlatformManufacturers,
} from '@/hooks/use-platform';
import type { InspectionStepTemplate } from '@/lib/types';
const ASSET_CATEGORIES = [
  { value: 'carabiner', label: 'Carabiner' },
  { value: 'harness', label: 'Harness' },
  { value: 'rope', label: 'Rope' },
  { value: 'lanyard', label: 'Lanyard' },
  { value: 'sling', label: 'Sling' },
  { value: 'descender', label: 'Descender' },
  { value: 'ascender', label: 'Ascender' },
  { value: 'pulley', label: 'Pulley' },
  { value: 'helmet', label: 'Helmet' },
  { value: 'anchor', label: 'Anchor' },
  { value: 'anchor_strap', label: 'Anchor Strap' },
  { value: 'connector', label: 'Connector' },
  { value: 'fall_arrester', label: 'Fall Arrester' },
  { value: 'fall_arrest_accessory', label: 'Fall Arrest Accessory' },
  { value: 'mobile_fall_arrester', label: 'Mobile Fall Arrester' },
  { value: 'rigging_plate', label: 'Rigging Plate' },
  { value: 'rope_clamp', label: 'Rope Clamp' },
  { value: 'swivel', label: 'Swivel' },
  { value: 'other', label: 'Other' },
];

export function GearModelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const gearModelId = parseInt(id || '0', 10);

  const { data: gearModel, isLoading, isError } = usePlatformGearModel(gearModelId);
  const { data: steps = [], isLoading: stepsLoading } = usePlatformInspectionSteps(gearModelId);
  const { data: manufacturers = [] } = usePlatformManufacturers();
  const updateMutation = useUpdatePlatformGearModel(gearModelId);
  const deleteMutation = useDeletePlatformGearModel();
  const createStepMutation = useCreatePlatformInspectionStep(gearModelId);
  const updateStepMutation = useUpdatePlatformInspectionStep(gearModelId);
  const deleteStepMutation = useDeletePlatformInspectionStep(gearModelId);
  const copyFromCategoryMutation = useCopyInspectionStepsFromCategory(gearModelId);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [deleteStepDialogOpen, setDeleteStepDialogOpen] = useState(false);
  const [stepToDelete, setStepToDelete] = useState<number | null>(null);
  const [addStepOpen, setAddStepOpen] = useState(false);
  const [newStepTitle, setNewStepTitle] = useState('');
  const [newStepDescription, setNewStepDescription] = useState('');
  const [newStepCritical, setNewStepCritical] = useState(false);

  // Form state for gear model fields
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [formDirty, setFormDirty] = useState(false);

  const getFieldValue = useCallback(
    (field: string) => {
      if (field in formData) return formData[field];
      if (!gearModel) return '';
      return (gearModel as unknown as Record<string, unknown>)[field] ?? '';
    },
    [formData, gearModel]
  );

  const setField = useCallback((field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormDirty(true);
  }, []);

  const handleSaveModel = useCallback(async () => {
    if (!formDirty || Object.keys(formData).length === 0) return;
    await updateMutation.mutateAsync(formData);
    setFormData({});
    setFormDirty(false);
  }, [formData, formDirty, updateMutation]);

  const handleDeleteModel = useCallback(async () => {
    await deleteMutation.mutateAsync(gearModelId);
    navigate('/platform/gear-models');
  }, [deleteMutation, gearModelId, navigate]);

  const handleAddStep = useCallback(async () => {
    if (!newStepTitle.trim()) return;
    await createStepMutation.mutateAsync({
      title: newStepTitle.trim(),
      description: newStepDescription.trim(),
      is_critical: newStepCritical,
    });
    setNewStepTitle('');
    setNewStepDescription('');
    setNewStepCritical(false);
    setAddStepOpen(false);
  }, [newStepTitle, newStepDescription, newStepCritical, createStepMutation]);

  const handleDeleteStep = useCallback(async () => {
    if (stepToDelete === null) return;
    await deleteStepMutation.mutateAsync(stepToDelete);
    setStepToDelete(null);
    setDeleteStepDialogOpen(false);
  }, [stepToDelete, deleteStepMutation]);

  const handleMoveStep = useCallback(
    async (stepId: number, currentNumber: number, direction: 'up' | 'down') => {
      const newNumber = direction === 'up' ? currentNumber - 1 : currentNumber + 1;
      if (newNumber < 1 || newNumber > steps.length) return;
      await updateStepMutation.mutateAsync({ stepId, step_number: newNumber });
    },
    [steps.length, updateStepMutation]
  );

  const handleToggleCritical = useCallback(
    async (stepId: number, isCritical: boolean) => {
      await updateStepMutation.mutateAsync({ stepId, is_critical: !isCritical });
    },
    [updateStepMutation]
  );

  const handleResetToDefaults = useCallback(async () => {
    await copyFromCategoryMutation.mutateAsync();
    setResetDialogOpen(false);
  }, [copyFromCategoryMutation]);

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError || !gearModel) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-8 w-8 text-orange-500 mx-auto mb-2" />
            <p className="text-gray-600">Gear model not found or failed to load.</p>
            <Button asChild className="mt-4" variant="outline">
              <Link to="/platform/gear-models">Back to Gear Models</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/platform/gear-models">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{gearModel.name}</h1>
            <p className="text-gray-600 text-sm">
              {gearModel.manufacturer_name} &middot; {gearModel.asset_category_display}
              {gearModel.model_number && <> &middot; {gearModel.model_number}</>}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSaveModel}
            disabled={!formDirty || updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
          <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Summary badges */}
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline">{gearModel.step_count} inspection steps</Badge>
        <Badge variant="outline">{gearModel.asset_count} assets using this model</Badge>
        {gearModel.is_verified && (
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Verified
          </Badge>
        )}
        {!gearModel.is_active && (
          <Badge variant="secondary">Inactive</Badge>
        )}
      </div>

      {/* Model Edit Form */}
      <Card>
        <CardHeader>
          <CardTitle>Gear Model Details</CardTitle>
          <CardDescription>Edit the gear model information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={getFieldValue('name') as string}
                onChange={(e) => setField('name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="manufacturer">Manufacturer *</Label>
              <Select
                value={String(getFieldValue('manufacturer'))}
                onValueChange={(value) => setField('manufacturer', parseInt(value, 10))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select manufacturer" />
                </SelectTrigger>
                <SelectContent>
                  {manufacturers.map((m) => (
                    <SelectItem key={m.id} value={String(m.id)}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="asset_category">Category *</Label>
              <Select
                value={getFieldValue('asset_category') as string}
                onValueChange={(value) => setField('asset_category', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="model_number">Model Number</Label>
              <Input
                id="model_number"
                value={getFieldValue('model_number') as string}
                onChange={(e) => setField('model_number', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="service_life_years">Service Life (years)</Label>
              <Input
                id="service_life_years"
                type="number"
                value={getFieldValue('service_life_years') as string}
                onChange={(e) =>
                  setField('service_life_years', e.target.value ? parseInt(e.target.value, 10) : null)
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="default_inspection_interval_days">Inspection Interval (days)</Label>
              <Input
                id="default_inspection_interval_days"
                type="number"
                value={getFieldValue('default_inspection_interval_days') as string}
                onChange={(e) =>
                  setField(
                    'default_inspection_interval_days',
                    e.target.value ? parseInt(e.target.value, 10) : null
                  )
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={getFieldValue('description') as string}
                onChange={(e) => setField('description', e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="product_url">Product URL</Label>
                <Input
                  id="product_url"
                  type="url"
                  value={getFieldValue('product_url') as string}
                  onChange={(e) => setField('product_url', e.target.value)}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_verified"
                  checked={getFieldValue('is_verified') as boolean}
                  onCheckedChange={(checked) => setField('is_verified', checked)}
                />
                <Label htmlFor="is_verified">Verified</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={getFieldValue('is_active') as boolean}
                  onCheckedChange={(checked) => setField('is_active', checked)}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inspection Steps Editor */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Inspection Step Templates</CardTitle>
              <CardDescription>
                Define the inspection steps for this gear model. Steps are performed in order.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setResetDialogOpen(true)}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset to Defaults
              </Button>
              <Button size="sm" onClick={() => setAddStepOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Step
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {stepsLoading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : steps.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <p>No inspection steps defined.</p>
              <p className="text-sm mt-1">
                Add steps manually or reset to category defaults.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {steps.map((step) => (
                <InspectionStepRow
                  key={step.id}
                  step={step}
                  totalSteps={steps.length}
                  onMove={(direction) => handleMoveStep(step.id, step.step_number, direction)}
                  onToggleCritical={() => handleToggleCritical(step.id, step.is_critical)}
                  onDelete={() => {
                    setStepToDelete(step.id);
                    setDeleteStepDialogOpen(true);
                  }}
                  isUpdating={updateStepMutation.isPending}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Gear Model Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Gear Model</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{gearModel.name}&rdquo;?
              {gearModel.asset_count > 0 && (
                <span className="block mt-2 text-red-600 font-medium">
                  This gear model has {gearModel.asset_count} associated asset(s) and cannot be
                  deleted.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteModel}
              disabled={gearModel.asset_count > 0 || deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset to Defaults Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset to Category Defaults</DialogTitle>
            <DialogDescription>
              This will replace all current inspection steps with the default steps for the{' '}
              {gearModel.asset_category_display} category. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleResetToDefaults}
              disabled={copyFromCategoryMutation.isPending}
            >
              {copyFromCategoryMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              Reset Steps
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Step Dialog */}
      <Dialog open={deleteStepDialogOpen} onOpenChange={setDeleteStepDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Inspection Step</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this inspection step? Remaining steps will be
              automatically renumbered.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteStepDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteStep}
              disabled={deleteStepMutation.isPending}
            >
              {deleteStepMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Delete Step
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Step Dialog */}
      <Dialog open={addStepOpen} onOpenChange={setAddStepOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Inspection Step</DialogTitle>
            <DialogDescription>
              Add a new inspection step. It will be added at the end of the list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="step-title">Title *</Label>
              <Input
                id="step-title"
                value={newStepTitle}
                onChange={(e) => setNewStepTitle(e.target.value)}
                placeholder="e.g., Visual Inspection - Webbing"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="step-description">Description</Label>
              <Textarea
                id="step-description"
                value={newStepDescription}
                onChange={(e) => setNewStepDescription(e.target.value)}
                placeholder="Detailed instructions for this inspection step..."
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="step-critical"
                checked={newStepCritical}
                onCheckedChange={setNewStepCritical}
              />
              <Label htmlFor="step-critical">Critical Step</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddStepOpen(false)}>
              Cancel
            </Button>
              <Button
                onClick={handleAddStep}
                data-validate-required="true"
                disabled={createStepMutation.isPending}
            >
              {createStepMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Add Step
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InspectionStepRow({
  step,
  totalSteps,
  onMove,
  onToggleCritical,
  onDelete,
  isUpdating,
}: {
  step: InspectionStepTemplate;
  totalSteps: number;
  onMove: (direction: 'up' | 'down') => void;
  onToggleCritical: () => void;
  onDelete: () => void;
  isUpdating: boolean;
}) {
  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border ${
        step.is_critical ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'
      }`}
    >
      {/* Step number and reorder controls */}
      <div className="flex flex-col items-center gap-1 min-w-[40px]">
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          disabled={step.step_number <= 1 || isUpdating}
          onClick={() => onMove('up')}
        >
          <ChevronUp className="h-3 w-3" />
        </Button>
        <span className="text-sm font-bold text-gray-500">{step.step_number}</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          disabled={step.step_number >= totalSteps || isUpdating}
          onClick={() => onMove('down')}
        >
          <ChevronDown className="h-3 w-3" />
        </Button>
      </div>

      {/* Step content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-sm">{step.title}</h4>
          {step.is_critical && (
            <Badge className="bg-red-100 text-red-800 text-xs">
              <ShieldAlert className="h-3 w-3 mr-1" />
              Critical
            </Badge>
          )}
        </div>
        {step.description && (
          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{step.description}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleCritical}
          disabled={isUpdating}
          title={step.is_critical ? 'Mark as non-critical' : 'Mark as critical'}
        >
          <ShieldAlert
            className={`h-4 w-4 ${step.is_critical ? 'text-red-600' : 'text-gray-400'}`}
          />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
